import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Button, Icon } from "semantic-ui-react";

import { Can } from "components";
import { ACL_ACTIVITIES, ACL_RESOURCES } from "models";
import { BrandGroupModal, BrandGroupsDropdown } from "modules/BrandsDashboard/components";
import "modules/BrandsDashboard/components/BrandsDashboardFilters/BrandsDashboardFilters.scss";
import { useExportBrandsMutation, useImportBrandsMutation } from "modules/BrandsDashboard/hooks";

const ODS_MIME = "application/vnd.oasis.opendocument.spreadsheet";

type BrandsDashboardFiltersProps = {
  selectedVinPrefixes: string[];
  onFilterChange: (vinPrefixes: string[]) => void;
};

export const BrandsDashboardFilters = ({ selectedVinPrefixes, onFilterChange }: BrandsDashboardFiltersProps) => {
  const { t } = useTranslation();

  const { mutateAsync: exportBrands, isPending: isExporting } = useExportBrandsMutation();
  const { mutateAsync: importBrands, isPending: isImporting } = useImportBrandsMutation();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleExport = () => exportBrands();

  const handleImportClick = () => fileInputRef.current?.click();

  const handleImportFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (file) {
      const isOds = file.name.toLowerCase().endsWith(".ods") && file.type === ODS_MIME;

      if (!isOds) toast.error(t("v8_file_must_be_ods", "The file must be an .ods file"));
      else {
        const formData = new FormData();
        formData.append("file", file);
        await importBrands(formData);
      }
    }

    event.target.value = "";
  };

  return (
    <>
      <div className="BrandsDashboardFilters">
        <div className="actions">
          <Can I={ACL_ACTIVITIES.Create} the={ACL_RESOURCES.BrandGroups}>
            <Button basic icon className="add-btn" onClick={() => setIsModalOpen(true)}>
              <Icon className="plus" />
            </Button>
          </Can>
          <Button basic icon loading={isImporting} disabled={isImporting} onClick={handleImportClick}>
            <Icon className="upload" />
          </Button>
          <Button basic icon loading={isExporting} disabled={isExporting} onClick={handleExport}>
            <Icon className="file export" />
          </Button>
        </div>
        <div className="filter">
          <BrandGroupsDropdown value={selectedVinPrefixes} handleOnChange={onFilterChange} />
        </div>
        <input ref={fileInputRef} type="file" className="file-input" accept={`.ods,${ODS_MIME}`} onChange={handleImportFile} />
      </div>
      <BrandGroupModal isOpen={isModalOpen} brandGroup={null} onClose={() => setIsModalOpen(false)} />
    </>
  );
};
